/* =========================================================
	PEOPLE CARDS
	For annual report pages (2024 and newer)
========================================================= */

// Updated people-cards.js with flexbox positioning logic
document.addEventListener('alpine:init', () => {
   if (typeof Alpine === 'undefined') {
      console.error('Alpine is not available in people-cards.js');
      return;
   }

   Alpine.data('peopleGrid', () => ({
      activeBio: null,
      resizeTimeout: null,
      bioStatusMessage: '',

      init() {
         // Handle escape key to close bio
         document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.activeBio) {
               this.closeBio();
            }
         });

         // Handle window resize to reposition active bio
         window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
               if (this.activeBio) {
                  this.repositionActiveBio();
               }
            }, 150);
         });
      },

      toggleBio(personId, event) {
         if (this.activeBio === personId) {
            this.closeBio();
            return;
         }

         // If switching between bios, handle transition smoothly
         if (this.activeBio) {
            this.switchBio(personId, event);
         } else {
            // Opening first bio
            this.openBio(personId, event);
         }
      },

      openBio(personId, event) {
         this.activeBio = personId;
         this.updateActiveCardClass(personId);

         const personName = this.getPersonName(personId);
         this.bioStatusMessage = `Biography opened for ${personName}`;

         this.$nextTick(() => {
            this.loadBio(event.currentTarget || event.target);
         });
      },

      switchBio(personId, event) {
         // First, fade out current bio content
         const bioRow = document.querySelector('.full-width-bio-row');
         const bioText = bioRow ? bioRow.querySelector('.bio-text') : null;

         if (bioText) {
            bioText.style.opacity = '0';
            bioText.style.transition = 'opacity 0.2s ease';
         }

         // Update active states
         this.activeBio = personId;
         this.updateActiveCardClass(personId);

         const personName = this.getPersonName(personId);
         this.bioStatusMessage = `Biography opened for ${personName}`;

         // After fade out, update content and position
         setTimeout(() => {
            this.$nextTick(() => {
               this.loadBio(event.currentTarget || event.target, true);
            });
         }, 200);
      },

      closeBio() {
         const wasActive = this.activeBio;
         this.clearActiveCardClasses();
         this.activeBio = null;

         // Remove bio row
         const bioRow = document.querySelector('.full-width-bio-row');
         if (bioRow) {
            bioRow.classList.remove('showing-bio');
         }

         if (wasActive) {
            const personName = this.getPersonName(wasActive);
            this.bioStatusMessage = `Biography closed for ${personName}`;
            this.returnFocusToButton(wasActive);
         }
      },

      loadBio(clickedButton, isSwitch = false) {
         let bioRow = document.querySelector('.full-width-bio-row');

         if (!bioRow) {
            bioRow = this.createBioRow();
         }

         const parentGrid = clickedButton.closest('.people-grid');
         const allCards = parentGrid.querySelectorAll('.people-card');
         const allCardsArray = Array.from(allCards);
         const personCard = clickedButton.closest('.people-card');
         const personIndex = allCardsArray.indexOf(personCard);

         const cardsPerRow = this.calculateCardsPerRow();
         let insertAfterIndex =
            personIndex + (cardsPerRow - (personIndex % cardsPerRow)) - 1;

         if (insertAfterIndex >= allCardsArray.length) {
            insertAfterIndex = allCardsArray.length - 1;
         }

         const cardToInsertAfter = allCardsArray[insertAfterIndex];

         // Only move the bio row if it's not already in the right position
         if (bioRow.previousElementSibling !== cardToInsertAfter) {
            cardToInsertAfter.after(bioRow);
         }

         // Get bio content
         const bioContent = document.getElementById(this.activeBio);
         const bioText = bioContent
            ? bioContent.innerHTML
            : 'Biography not found.';

         // Update bio content
         const bioTextContainer = bioRow.querySelector('.bio-text');
         bioTextContainer.innerHTML = bioText;

         if (!isSwitch) {
            bioRow.classList.add('showing-bio');
         } else {
            // For switches, just fade the content back in
            bioTextContainer.style.opacity = '1';
            bioTextContainer.style.transition = 'opacity 0.3s ease';
         }

         this.$nextTick(() => {
            this.focusBioContent();
         });
      },

      calculateCardsPerRow() {
         // Get the actual grid container and measure it
         const grid = document.querySelector('.people-grid');
         if (!grid) return 1;

         const gridWidth = grid.getBoundingClientRect().width;
         const windowWidth = window.innerWidth;

         // Get the actual card width and gap from CSS
         let cardWidth, gap;

         if (windowWidth >= 768) {
            cardWidth = 182;
            gap =
               parseFloat(
                  getComputedStyle(document.documentElement).getPropertyValue(
                     '--grid-col-gap'
                  )
               ) || 60; // 3.75rem default
         } else {
            cardWidth = 150;
            gap =
               parseFloat(
                  getComputedStyle(document.documentElement).getPropertyValue(
                     '--grid-col-gap'
                  )
               ) || 20; // 1.25rem default
         }

         // Calculate how many cards actually fit
         const cardsPerRow = Math.floor((gridWidth + gap) / (cardWidth + gap));

         return Math.max(1, cardsPerRow);
      },

      createBioRow() {
         const row = document.createElement('div');
         row.className = 'full-width-bio-row';

         row.innerHTML = `
            <div class="bio-content-wrapper">
               <div class="bio-text"></div>
               <button class="bio-close" aria-label="Close biography">
                  <span class="sr-only">Close Bio</span>
                  <span class="icon-close" aria-hidden="true">
                     <span></span>
                     <span></span>
                  </span>
               </button>
            </div>
         `;

         // Add close button functionality
         const closeButton = row.querySelector('.bio-close');
         closeButton.addEventListener('click', () => {
            this.closeBio();
         });

         return row;
      },

      repositionActiveBio() {
         if (!this.activeBio) return;

         const clickedButton = document.querySelector(
            `[data-person-id="${this.activeBio}"]`
         );
         if (clickedButton) {
            this.loadBio(clickedButton, true);
            this.updateActiveCardClass(this.activeBio);
         }
      },

      focusBioContent() {
         const bioRow = document.querySelector('.full-width-bio-row .bio-text');
         if (bioRow) {
            bioRow.setAttribute('tabindex', '-1');
            bioRow.focus();
         }
      },

      updateActiveCardClass(activePersonId) {
         this.clearActiveCardClasses();
         const activeButton = document.querySelector(
            `[data-person-id="${activePersonId}"]`
         );
         if (activeButton) {
            const activeCard = activeButton.closest('.people-card');
            if (activeCard) {
               activeCard.classList.add('bio-active');
            }
         }
      },

      clearActiveCardClasses() {
         const activeCards = document.querySelectorAll(
            '.people-card.bio-active'
         );
         activeCards.forEach((card) => {
            card.classList.remove('bio-active');
         });
      },

      returnFocusToButton(personId) {
         this.$nextTick(() => {
            const button = document.querySelector(
               `[data-person-id="${personId}"]`
            );
            if (button) {
               button.focus();
            }
         });
      },

      getPersonName(personId) {
         const button = document.querySelector(
            `[data-person-id="${personId}"]`
         );
         if (button) {
            const nameElement = button.querySelector('h3');
            return nameElement ? nameElement.textContent : 'person';
         }
         return 'person';
      },
   }));
});
